import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import React, { useState } from 'react';
import { Palette } from 'Palette';
import { CHAT_WINDOW } from 'DesignTokens';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import { FundOpportunityLabels } from '../Util/Labels';
import { EditableFieldValueCell } from './EditableFieldValueCell';
import { FundOpportunityFieldReasoningIcon } from './FundOpportunityFieldReasoningIcon';
import { MissingFundOpportunityFieldRow } from './MissingFundOpportunityFieldRow';
import { PopulatedFundOpportunityAddressRows } from './PopulatedFundOpportunityAddressRows';
import {
  formatPopulatedFieldValue,
  getEditableFieldInputType,
  isFieldEditable,
  preferredFieldOrder,
} from './populatedFundOpportunityFieldFormatters';
import { usePopulatedFundOpportunityFieldAnimation } from './usePopulatedFundOpportunityFieldAnimation';

function fieldValue(field: unknown): unknown {
  if (field && typeof field === 'object' && 'value' in field) {
    return (field as { value: unknown }).value;
  }
  return field;
}

function isEmptyFieldValue(field: unknown): boolean {
  const val = fieldValue(field);
  return (
    val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)
  );
}

const PopulatedFundOpportunityFieldsPreview: React.FC<{
  data?: FundOpportunityChatResponse['currentData'];
  missingRequiredFields?: string[];
  isCreating?: boolean;
  onComplete: () => void;
  onRemoveField?: (fieldName: string) => void;
  onEditField?: (fieldName: string, newValue: string | number) => void;
  onRemoveAddress?: (addressIndex: number) => void;
  onRemoveAddresses?: (addressIndices: number[]) => void;
  onSetField?: (fieldName: string, value: string | number | string[]) => void;
}> = ({
  data,
  missingRequiredFields,
  isCreating = false,
  onComplete,
  onRemoveField,
  onEditField,
  onRemoveAddress,
  onRemoveAddresses,
  onSetField,
}) => {
  const { displayedFieldLines, animatedKeys } = usePopulatedFundOpportunityFieldAnimation(data);
  const [selectedAddressIndices, setSelectedAddressIndices] = useState<Set<number>>(new Set());

  if (!data) {
    return null;
  }

  const fields = data;
  const addresses = fields.addresses || [];
  const requiredMissing = new Set(missingRequiredFields || []);
  const canCreate = !isEmptyFieldValue(fields.name) && !isEmptyFieldValue(fields.summaryTitle);
  // The leading column only ever holds content (a checkbox) on address
  // rows — collapse it the rest of the time instead of leaving a dead 48px
  // strip down the left of the table.
  const leadingColumnWidth = addresses.length > 0 ? 48 : 0;

  const handleSelectAddress = (index: number) => {
    setSelectedAddressIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAllAddresses = () => {
    if (selectedAddressIndices.size === addresses.length) {
      setSelectedAddressIndices(new Set());
    } else {
      setSelectedAddressIndices(new Set(addresses.map((_, idx) => idx)));
    }
  };

  const handleDeleteSelectedAddresses = () => {
    if (onRemoveAddresses && selectedAddressIndices.size > 0) {
      onRemoveAddresses(Array.from(selectedAddressIndices).sort((a, b) => b - a));
      setSelectedAddressIndices(new Set());
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        // border-box so the 1px border counts toward this flex item's
        // stretched height instead of adding 2px past it — keeps this
        // card's bottom edge exactly matching the chat card's.
        boxSizing: 'border-box',
        px: 3,
        pt: 2,
        pb: 2,
        backgroundColor: Palette.tone1.background,
        border: `1px solid ${Palette.default.border}`,
        // Matches GenericChatWindow's unifiedComposer card radius so the two
        // side-by-side sections read as one consistent pair.
        borderRadius: CHAT_WINDOW.messageAreaRadius,
        overflow: 'hidden',
      }}
    >
      <TableContainer
        component={Paper}
        sx={{ backgroundColor: 'transparent', boxShadow: 'none', flex: 1, overflowY: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: Palette.tone2.background }}>
              <TableCell
                sx={{
                  width: leadingColumnWidth,
                  px: leadingColumnWidth ? undefined : 0,
                }}
              />
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text, width: '60%' }}>
                Value
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {preferredFieldOrder.map((key, index) => {
              if (key === 'addresses') {
                if (addresses.length > 0) {
                  return (
                    <React.Fragment key={key}>
                      <PopulatedFundOpportunityAddressRows
                        addresses={addresses}
                        displayedFieldLines={displayedFieldLines}
                        animatedKeys={animatedKeys}
                        selectedAddressIndices={selectedAddressIndices}
                        onSelectAddress={handleSelectAddress}
                        onSelectAll={handleSelectAllAddresses}
                        onDeleteSelected={handleDeleteSelectedAddresses}
                        onRemoveAddress={onRemoveAddress}
                        onRemoveAddresses={onRemoveAddresses}
                      />
                    </React.Fragment>
                  );
                }
                return onSetField ? (
                  <MissingFundOpportunityFieldRow
                    key={key}
                    fieldName={key}
                    index={index}
                    required={requiredMissing.has(key)}
                    onSetField={onSetField}
                  />
                ) : null;
              }

              const field = fields[key as keyof typeof fields];
              if (isEmptyFieldValue(field)) {
                return onSetField ? (
                  <MissingFundOpportunityFieldRow
                    key={key}
                    fieldName={key}
                    index={index}
                    required={requiredMissing.has(key)}
                    onSetField={onSetField}
                  />
                ) : null;
              }

              const fullLine = displayedFieldLines[key];
              const label = FundOpportunityLabels[key] || key;
              const isAnimating = animatedKeys.has(key);

              return (
                <TableRow
                  key={key}
                  sx={{
                    backgroundColor: index % 2 === 0 ? Palette.tone1.background : 'white',
                    '&:hover': { backgroundColor: Palette.tone1.activeBg },
                  }}
                >
                  <TableCell
                    sx={{ width: leadingColumnWidth, px: leadingColumnWidth ? undefined : 0 }}
                  />
                  <TableCell sx={{ fontWeight: 'bold', color: Palette.tone1.text }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      {label}
                      <FundOpportunityFieldReasoningIcon field={field} />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: Palette.tone1.text,
                      whiteSpace: 'pre-line',
                      opacity: isAnimating ? 0.7 : 1,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {onEditField && isFieldEditable(key) ? (
                      <EditableFieldValueCell
                        rawValue={fieldValue(field)}
                        displayValue={fullLine || formatPopulatedFieldValue(field, key)}
                        inputType={getEditableFieldInputType(key)}
                        onCommit={(newValue) => onEditField(key, newValue)}
                      />
                    ) : (
                      fullLine || formatPopulatedFieldValue(field, key)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {onRemoveField && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveField(key)}
                        sx={{ color: Palette.warning.buttonBg }}
                        disabled={isCreating}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <StyledButtonGroup sx={{ mt: 2, flexShrink: 0 }}>
        <StyledButton
          disabled={!canCreate || isCreating}
          onClick={onComplete}
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isCreating ? 'Creating Fund Opportunity…' : 'Create Fund Opportunity'}
        </StyledButton>
      </StyledButtonGroup>
    </Box>
  );
};

export default PopulatedFundOpportunityFieldsPreview;
